import { createMemo } from "solid-js";
import type {
  CvCandidate,
  CvSelection,
} from "@/domains/company/anonymization/types";
import { MAX_CV_SELECTION } from "@/domains/company/anonymization/types";
import {
  type SelectedFreelancer,
  useFreelanceSearchStore,
} from "../store/freelance-search-store";

/**
 * Selezione dei candidati per il download dei CV anonimi — la metà "ricerca
 * attiva" del contratto {@link CvSelection}.
 *
 * Gemella della selezione "Contatta" (`store.selectedFreelancers`) — stesso
 * tetto, stessa forma delle entry — ma su un campo separato, così le due CTA
 * del dettaglio non si contendono gli stessi slot. Vive nello store persistito
 * perché qui il dettaglio è uno sheet da cui si può navigare via: la selezione
 * deve sopravvivere all'andata e ritorno. Il pool, che non ha quel giro, usa
 * `useLocalCvSelection`.
 *
 * Tutta la scrittura su `selectedForCv` passa da qui: un solo posto conosce il
 * tetto, il dedup e il confronto degli id per stringa (gli id arrivano come
 * number da alcune sorgenti di ricerca e come string dallo store persistito).
 */
export const useCvSelection = (): CvSelection => {
  const [store, setStore] = useFreelanceSearchStore();

  /**
   * Lista corrente. Il fallback a `[]` non è difensivismo: uno store persistito
   * prima di questa feature si reidrata senza la chiave, quindi il primo render
   * dopo l'aggiornamento la legge `undefined`.
   */
  const selected = createMemo<SelectedFreelancer[]>(
    () => store.selectedForCv ?? [],
  );

  const count = createMemo(() => selected().length);
  const remaining = createMemo(() => MAX_CV_SELECTION - count());
  const isFull = createMemo(() => count() >= MAX_CV_SELECTION);

  const isSelected = (id: string | number) =>
    selected().some((f) => String(f.id) === String(id));

  /** No-op se il candidato c'è già o se gli slot sono pieni. */
  const add = (candidate: CvCandidate) => {
    const idStr = String(candidate.id);
    setStore("selectedForCv", (current) => {
      const list = current ?? [];
      if (list.some((f) => String(f.id) === idStr)) return list;
      if (list.length >= MAX_CV_SELECTION) return list;
      return [
        ...list,
        {
          id: idStr,
          full_name: candidate.full_name ?? "",
          avatar_url: candidate.avatar_url ?? "",
          job_title: candidate.job_title ?? "",
          /* `already_applied` non è parte del contratto CV (la barra non lo
             mostra): lo store lo vuole, e per la selezione CV non è mai un
             discriminante — si scarica il CV anche di chi si è già candidato. */
          already_applied: false,
        },
      ];
    });
  };

  const remove = (id: string | number) => {
    const idStr = String(id);
    setStore("selectedForCv", (current) =>
      (current ?? []).filter((f) => String(f.id) !== idStr),
    );
  };

  const clear = () => {
    setStore("selectedForCv", []);
  };

  return { selected, count, remaining, isFull, isSelected, add, remove, clear };
};
